import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';

export default function ParentSignUp() {
  const navigate = useNavigate();
  const { signupParent } = useAuth();
  const [form, setForm] = useState({ name: '', phone: '', password: '', student_code: '', relationship: 'Parent' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.password || !form.student_code) { toast.error('Please fill all required fields'); return; }
    setLoading(true);
    try {
      await signupParent(form);
      toast.success('Account created! Monitoring your child now.');
      navigate('/parent', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col px-4 py-6" style={{ background: '#FFF8F0' }}>
      <button data-testid="parent-signup-back" onClick={() => navigate('/')} className="self-start mb-4">
        <ArrowLeft size={24} color="#1B4F8A" />
      </button>

      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-6">
          <span className="text-4xl block mb-1">👨‍👧</span>
          <h1 className="font-fredoka text-2xl" style={{ color: '#1B4F8A' }}>Parent Sign Up</h1>
          <p className="text-sm font-semibold" style={{ color: '#8B7355' }}>Monitor your child's math progress</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: '#1B4F8A' }}>Full Name *</label>
            <input data-testid="parent-name-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Your full name"
              className="w-full bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none focus:border-[#1B4F8A] transition-all" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
          </div>

          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: '#1B4F8A' }}>Mobile Number *</label>
            <div className="flex gap-2">
              <span className="bg-white border-2 rounded-2xl px-3 py-3 font-bold text-xs flex items-center" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }}>+91</span>
              <input data-testid="parent-phone-input" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="9876543210" maxLength={10}
                className="flex-1 bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none focus:border-[#1B4F8A] transition-all" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: '#1B4F8A' }}>Password *</label>
            <div className="relative">
              <input data-testid="parent-password-input" type={showPw ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)} placeholder="Create a password"
                className="w-full bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none focus:border-[#1B4F8A] transition-all pr-12" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3.5" style={{ color: '#8B7355' }}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: '#1B4F8A' }}>Child's Student Code *</label>
            <input data-testid="parent-student-code-input" value={form.student_code} onChange={e => set('student_code', e.target.value)}
              placeholder="6-digit code from child's profile" maxLength={6}
              className="w-full bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none focus:border-[#1B4F8A] transition-all" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
            <p className="text-xs mt-1" style={{ color: '#8B7355' }}>Ask your child for their Student Code from the Profile screen</p>
          </div>

          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: '#1B4F8A' }}>Relationship</label>
            <select data-testid="parent-relationship-select" value={form.relationship} onChange={e => set('relationship', e.target.value)}
              className="w-full bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none focus:border-[#1B4F8A] transition-all" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }}>
              <option value="Parent">Parent</option>
              <option value="Guardian">Guardian</option>
              <option value="Relative">Relative</option>
            </select>
          </div>

          <button data-testid="parent-signup-submit" type="submit" disabled={loading}
            className="btn-press w-full py-4 rounded-full font-extrabold text-white text-base uppercase tracking-wide"
            style={{ background: '#1B4F8A', boxShadow: '0 4px 0 #143D6E', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Creating Account...' : 'Join as Parent'}
          </button>
        </form>

        <p className="text-center text-xs font-semibold mt-4" style={{ color: '#8B7355' }}>
          Already have an account? <button onClick={() => navigate('/login')} className="underline" style={{ color: '#1B4F8A' }} data-testid="parent-to-login">Login</button>
        </p>
      </div>
    </div>
  );
}
